import { useId } from 'react';
import type { SparkLegendItem, SparkMark, SparkPoint, SparkRun } from '@tv-casino/viewmodels/components/memberMarketModel.js';
import styles from './Sparkline.module.css';

const SW = 280;
const SH = 72;
const PAD = 4;
/** Room above the line for chip-move labels, below for the low's. */
const PAD_TOP = 14;
const PAD_BOTTOM = 8;
const MARK_GAP = 5;

/** Keep a label inside the chart near either edge. */
const anchorAt = (f: number): 'start' | 'middle' | 'end' =>
  f < 0.1 ? 'start' : f > 0.9 ? 'end' : 'middle';

/**
 * One balance line in the market-desk style: coloured by the game that drew
 * each stretch, with the high, the low and house chip moves pinned to it.
 * Pure render — the points, runs and marks come from `memberMarketModel`.
 */
export function Sparkline({
  points,
  runs,
  marks,
  single,
  label,
  baseline = null,
  fill = false,
}: {
  points: readonly SparkPoint[];
  runs: readonly SparkRun[];
  marks: readonly SparkMark[];
  single: boolean;
  label: string;
  /** Opening balance on the line's 0–1 scale: the water the night floats on. */
  baseline?: number | null;
  /** Wash the night: above the water green, the dips below it red. */
  fill?: boolean;
}) {
  const clipId = useId();
  if (points.length === 0) return null;

  const px = (f: number): number => PAD + f * (SW - PAD * 2);
  const py = (f: number): number => PAD_TOP + (1 - f) * (SH - PAD_TOP - PAD_BOTTOM);
  const water = baseline === null ? null : py(baseline);
  const area =
    fill && water !== null && points.length > 1
      ? [
          `${px(points[0]!.x)},${water}`,
          ...points.map((p) => `${px(p.x)},${py(p.y)}`),
          `${px(points[points.length - 1]!.x)},${water}`,
        ].join(' ')
      : null;

  return (
    <svg className={styles.spark} viewBox={`0 0 ${SW} ${SH}`} role="img" aria-label={label}>
      {area && water !== null && (
        <>
          <defs>
            <clipPath id={`${clipId}-up`}>
              <rect x={0} y={0} width={SW} height={Math.max(0, water)} />
            </clipPath>
            <clipPath id={`${clipId}-down`}>
              <rect x={0} y={water} width={SW} height={Math.max(0, SH - water)} />
            </clipPath>
          </defs>
          <polygon className={styles.areaUp} points={area} clipPath={`url(#${clipId}-up)`} />
          <polygon className={styles.areaDown} points={area} clipPath={`url(#${clipId}-down)`} />
        </>
      )}
      {water !== null && (
        <line
          className={styles.water}
          x1={px(0)}
          x2={px(1)}
          y1={water}
          y2={water}
          stroke="currentColor"
          strokeDasharray="3 4"
        />
      )}
      {single ? (
        <circle cx={px(points[0]!.x)} cy={py(points[0]!.y)} r={3} fill="currentColor" />
      ) : (
        runs.map((run, i) => (
          <polyline
            key={i}
            className={styles[`tone_${run.tone}`]}
            points={run.points.map((p) => `${px(p.x)},${py(p.y)}`).join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))
      )}
      {marks
        .filter((m) => m.kind === 'chipsIn' || m.kind === 'chipsOut')
        .map((m, i) => (
          <g key={`move-${i}`} className={styles.markMove}>
            <title>{m.title}</title>
            <line
              x1={px(m.x)}
              x2={px(m.x)}
              y1={PAD_TOP - 2}
              y2={SH - PAD_BOTTOM + 2}
              stroke="currentColor"
              strokeDasharray="2 2"
            />
            <circle cx={px(m.x)} cy={py(m.y)} r={3} fill="currentColor" />
            <text
              className={m.kind === 'chipsIn' ? styles.win : styles.loss}
              x={px(m.x)}
              y={PAD_TOP - 5}
              textAnchor={anchorAt(m.x)}
            >
              {m.label}
            </text>
          </g>
        ))}
      {marks
        .filter((m) => m.kind === 'high' || m.kind === 'low')
        .map((m) => {
          // Beside the dot, on whichever side has more room.
          const right = m.x < 0.5;
          return (
            <g key={m.kind} className={styles.markExtreme}>
              <title>{m.title}</title>
              <circle cx={px(m.x)} cy={py(m.y)} r={2.5} />
              <text
                x={px(m.x) + (right ? MARK_GAP : -MARK_GAP)}
                y={py(m.y) + (m.kind === 'high' ? 3 : 1)}
                textAnchor={right ? 'start' : 'end'}
              >
                {m.kind === 'high' ? 'H' : 'L'} {m.label}
              </text>
            </g>
          );
        })}
    </svg>
  );
}

/** The key for the games on one or more sparklines; nothing when there is one game. */
export function SparkLegend({ items }: { items: readonly SparkLegendItem[] }) {
  if (items.length === 0) return null;
  return (
    <ul className={styles.sparkLegend} aria-label="Games on the line">
      {items.map((item) => (
        <li key={item.tone} className={styles[`tone_${item.tone}`]}>
          {item.label}
        </li>
      ))}
    </ul>
  );
}
